import React, { useState } from 'react';
import { Coffee, Clock, ChevronDown, ChevronUp, Utensils, Info } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения примерного плана питания на день
 */
const DailyMealPlan = ({ results, formData, dietTypes }) => {
  const [expandedMeal, setExpandedMeal] = useState(null);
  
  if (!results || !results.mealDistribution) return null;

  // Получаем информацию о выбранном типе диеты
  const selectedDiet = dietTypes.find(diet => diet.id === formData.dietType) || dietTypes[0];
  
  // Функция для генерации рекомендаций по питанию на основе типа диеты
  const getDietRecommendations = (dietType) => {
    switch (dietType) {
      case 'balanced':
        return {
          proteinSources: ['Курица', 'Индейка', 'Рыба', 'Творог', 'Яйца', 'Греческий йогурт', 'Бобовые'],
          fatSources: ['Оливковое масло', 'Авокадо', 'Орехи', 'Семена льна', 'Рыбий жир'],
          carbSources: ['Овсянка', 'Гречка', 'Киноа', 'Бурый рис', 'Цельнозерновой хлеб', 'Фрукты', 'Овощи']
        };
      case 'low_carb':
        return {
          proteinSources: ['Говядина', 'Свинина', 'Курица', 'Индейка', 'Рыба', 'Яйца', 'Творог'],
          fatSources: ['Сливочное масло', 'Оливковое масло', 'Авокадо', 'Орехи', 'Сыр', 'Сметана'],
          carbSources: ['Зеленые овощи', 'Брокколи', 'Цветная капуста', 'Кабачки', 'Ягоды']
        };
      case 'high_protein':
        return {
          proteinSources: ['Протеиновый коктейль', 'Куриная грудка', 'Говядина', 'Рыба', 'Яичные белки', 'Творог', 'Тунец'],
          fatSources: ['Оливковое масло', 'Авокадо', 'Орехи', 'Льняное масло'],
          carbSources: ['Овсянка', 'Бурый рис', 'Батат', 'Фрукты', 'Овощи']
        };
      case 'keto':
        return {
          proteinSources: ['Жирное мясо', 'Сало', 'Рыба', 'Яйца', 'Сыр', 'Творог'],
          fatSources: ['Сливочное масло', 'Сало', 'Кокосовое масло', 'Оливковое масло', 'Авокадо', 'Сыр', 'Сливки'],
          carbSources: ['Зеленые листовые овощи', 'Авокадо', 'Оливки', 'Небольшое количество ягод']
        };
      default:
        return {
          proteinSources: ['Курица', 'Индейка', 'Рыба', 'Творог', 'Яйца'],
          fatSources: ['Оливковое масло', 'Авокадо', 'Орехи', 'Семена'],
          carbSources: ['Крупы', 'Хлеб', 'Фрукты', 'Овощи']
        };
    }
  };

  const dietRecommendations = getDietRecommendations(formData.dietType);
  
  // Функция для генерации примерного плана питания
  const generateMealPlan = (mealDistribution, macros, dietRecommendations) => {
    return mealDistribution.map(meal => {
      // Рассчитываем макронутриенты для этого приема пищи
      const mealRatio = meal.calories / results.targetCalories;
      const mealProtein = Math.round(macros.protein * mealRatio);
      const mealFat = Math.round(macros.fat * mealRatio);
      const mealCarbs = Math.round(macros.carbs * mealRatio);
      
      // Генерируем примерный список продуктов
      let foodItems = [];
      
      if (meal.isMain) {
        // Для основных приемов пищи
        if (meal.name === 'Завтрак') {
          foodItems = generateBreakfastItems(mealProtein, mealFat, mealCarbs, dietRecommendations, formData.dietType);
        } else if (meal.name === 'Обед') {
          foodItems = generateLunchItems(mealProtein, mealFat, mealCarbs, dietRecommendations, formData.dietType);
        } else { // Ужин
          foodItems = generateDinnerItems(mealProtein, mealFat, mealCarbs, dietRecommendations, formData.dietType);
        }
      } else {
        // Для перекусов
        foodItems = generateSnackItems(mealProtein, mealFat, mealCarbs, dietRecommendations, formData.dietType);
      }
      
      return {
        ...meal,
        macros: {
          protein: mealProtein,
          fat: mealFat,
          carbs: mealCarbs
        },
        foodItems
      };
    });
  };
  
  // Функции генерации примерных блюд для разных приемов пищи
  const generateBreakfastItems = (protein, fat, carbs, recommendations, dietType) => {
    if (dietType === 'keto') {
      return [
        'Омлет из 3 яиц с сыром и авокадо',
        'Кофе с кокосовым маслом или сливками',
        'Горсть орехов'
      ];
    } else if (dietType === 'low_carb') {
      return [
        'Яичница из 2-3 яиц с овощами',
        'Творог с орехами и ягодами',
        'Несладкий чай или кофе'
      ];
    } else if (dietType === 'high_protein') {
      return [
        'Протеиновый коктейль с молоком',
        'Омлет из яичных белков с овощами',
        'Овсянка на воде с протеином'
      ];
    } else {
      return [
        'Овсянка с фруктами и орехами',
        'Творог с ягодами и медом',
        'Цельнозерновой тост с авокадо и яйцом'
      ];
    }
  };
  
  const generateLunchItems = (protein, fat, carbs, recommendations, dietType) => {
    if (dietType === 'keto') {
      return [
        'Стейк из говядины с салатом из авокадо и оливок',
        'Котлеты из фарша с сыром и цветной капустой',
        'Бульон с курицей и зелеными овощами'
      ];
    } else if (dietType === 'low_carb') {
      return [
        'Куриная грудка с овощами на гриле',
        'Рыба запеченная с овощами',
        'Мясной салат с оливковым маслом'
      ];
    } else if (dietType === 'high_protein') {
      return [
        'Куриная грудка (200 г) с гарниром из бурого риса',
        'Фарш индейки с овощами',
        'Рыба на пару с овощами и киноа'
      ];
    } else {
      return [
        'Гречка с куриной грудкой и овощами',
        'Суп из чечевицы с овощами',
        'Рыба на пару с рисом и салатом'
      ];
    }
  };
  
  const generateDinnerItems = (protein, fat, carbs, recommendations, dietType) => {
    if (dietType === 'keto') {
      return [
        'Запеченная рыба с авокадо и зеленым салатом',
        'Мясо с грибами в сливочном соусе',
        'Салат с тунцом, оливками и оливковым маслом'
      ];
    } else if (dietType === 'low_carb') {
      return [
        'Запеченная рыба с овощами',
        'Творог с орехами и зеленью',
        'Белковый омлет с овощами'
      ];
    } else if (dietType === 'high_protein') {
      return [
        'Творог с протеином и орехами',
        'Рыба на пару с овощами',
        'Куриные котлеты с салатом'
      ];
    } else {
      return [
        'Рыба запеченная с овощами',
        'Куриная грудка с овощами и киноа',
        'Творог с фруктами и орехами'
      ];
    }
  };
  
  const generateSnackItems = (protein, fat, carbs, recommendations, dietType) => {
    if (dietType === 'keto') {
      return [
        'Горсть орехов и семечек',
        'Сыр твердых сортов',
        'Авокадо с оливковым маслом'
      ];
    } else if (dietType === 'low_carb') {
      return [
        'Греческий йогурт с орехами',
        'Вареные яйца',
        'Овощные палочки с творожным сыром'
      ];
    } else if (dietType === 'high_protein') {
      return [
        'Протеиновый коктейль',
        'Греческий йогурт',
        'Творожный десерт с протеином'
      ];
    } else {
      return [
        'Фрукты с йогуртом',
        'Орехи и сухофрукты',
        'Творожная запеканка'
      ];
    }
  };
  
  // Генерируем план питания
  const mealPlan = generateMealPlan(results.mealDistribution, results.macros, dietRecommendations);
  
  const toggleMeal = (mealIndex) => {
    if (expandedMeal === mealIndex) {
      setExpandedMeal(null);
    } else {
      setExpandedMeal(mealIndex);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Coffee className="w-5 h-5 mr-2 text-emerald-500" />
          Примерный план питания
        </h3>
        <span className="text-sm bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 py-1 px-3 rounded-full">
          {selectedDiet.name}
        </span>
      </div>

      {/* Информационный блок */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Об этом плане питания
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Это примерный план с ориентировочным распределением калорий и макронутриентов. 
              Для составления индивидуального рациона с учетом ваших пищевых предпочтений, 
              аллергий и доступных продуктов рекомендуем проконсультироваться с диетологом.
            </p>
          </div>
        </div>
      </div>

      {/* Основные рекомендации по продуктам */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg border border-emerald-100 dark:border-emerald-800">
          <h5 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2">
            Источники белка
          </h5>
          <ul className="text-xs text-emerald-600 dark:text-emerald-400 space-y-1">
            {dietRecommendations.proteinSources.map((source, index) => (
              <li key={`protein-${index}`} className="flex items-center">
                <div className="w-3 h-3 bg-emerald-200 dark:bg-emerald-700 rounded-full mr-2"></div>
                {source}
              </li>
            ))}
          </ul>
        </div>
        
        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-800">
          <h5 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">
            Источники жиров
          </h5>
          <ul className="text-xs text-amber-600 dark:text-amber-400 space-y-1">
            {dietRecommendations.fatSources.map((source, index) => (
              <li key={`fat-${index}`} className="flex items-center">
                <div className="w-3 h-3 bg-amber-200 dark:bg-amber-700 rounded-full mr-2"></div>
                {source}
              </li>
            ))}
          </ul>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
          <h5 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
            Источники углеводов
          </h5>
          <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
            {dietRecommendations.carbSources.map((source, index) => (
              <li key={`carb-${index}`} className="flex items-center">
                <div className="w-3 h-3 bg-blue-200 dark:bg-blue-700 rounded-full mr-2"></div>
                {source}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Список приемов пищи */}
      <div className="space-y-3">
        {mealPlan.map((meal, index) => (
          <div 
            key={`meal-${index}`} 
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            {/* Заголовок приема пищи */}
            <div 
              className={`px-4 py-3 flex items-center justify-between cursor-pointer
                ${expandedMeal === index ? 'bg-gray-100 dark:bg-gray-700/70' : 'bg-white dark:bg-gray-800'}
                ${meal.isMain ? 'border-l-4 border-emerald-500' : 'border-l-4 border-purple-400'}
              `}
              onClick={() => toggleMeal(index)}
            >
              <div className="flex items-center">
                {meal.isMain ? (
                  <Utensils className="w-4 h-4 text-emerald-500 mr-2" />
                ) : (
                  <Coffee className="w-4 h-4 text-purple-500 mr-2" />
                )}
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {meal.name}
                </h4>
                <span className="ml-4 text-sm text-gray-500 dark:text-gray-400 flex items-center">
                  <Clock className="w-3.5 h-3.5 mr-1" />
                  {index === 0 ? '7:00-9:00' : 
                   index === 1 ? '12:00-14:00' : 
                   index === 2 ? '18:00-20:00' : 
                   `${10 + index * 2}:00`}
                </span>
              </div>
              <div className="flex items-center">
                <span className="mr-4 font-medium text-gray-800 dark:text-gray-200">
                  {formatNumber(meal.calories, 0)} ккал
                </span>
                {expandedMeal === index ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
            </div>
            
            {/* Детали приема пищи (раскрывающаяся часть) */}
            {expandedMeal === index && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                {/* Макронутриенты */}
                <div className="flex space-x-4 mb-3 text-sm">
                  <div className="bg-emerald-100 dark:bg-emerald-900/30 px-3 py-1 rounded-full text-emerald-800 dark:text-emerald-300">
                    Б: {meal.macros.protein} г
                  </div>
                  <div className="bg-amber-100 dark:bg-amber-900/30 px-3 py-1 rounded-full text-amber-800 dark:text-amber-300">
                    Ж: {meal.macros.fat} г
                  </div>
                  <div className="bg-blue-100 dark:bg-blue-900/30 px-3 py-1 rounded-full text-blue-800 dark:text-blue-300">
                    У: {meal.macros.carbs} г
                  </div>
                </div>
                
                {/* Примерные блюда */}
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Примеры блюд:
                </h5>
                <ul className="space-y-2">
                  {meal.foodItems.map((item, itemIndex) => (
                    <li 
                      key={`item-${index}-${itemIndex}`}
                      className="flex items-start text-sm text-gray-600 dark:text-gray-400"
                    >
                      <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-400 mr-2 mt-0.5 flex-shrink-0">
                        {itemIndex + 1}
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-500 italic">
                  Примечание: это предложения блюд, которые можно адаптировать под ваши предпочтения. 
                  Важно соблюдать приблизительное количество калорий и соотношение БЖУ.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
          Общие рекомендации по режиму питания:
        </h4>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start">
            <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full mr-2 mt-0.5 flex-shrink-0"></div>
            <p>Старайтесь придерживаться приблизительного времени приемов пищи для нормализации метаболизма</p>
          </li>
          <li className="flex items-start">
            <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full mr-2 mt-0.5 flex-shrink-0"></div>
            <p>Пейте достаточное количество воды — {results.waterRecommendation} литра в день</p>
          </li>
          <li className="flex items-start">
            <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full mr-2 mt-0.5 flex-shrink-0"></div>
            <p>Последний прием пищи желательно за 2-3 часа до сна</p>
          </li>
          <li className="flex items-start">
            <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full mr-2 mt-0.5 flex-shrink-0"></div>
            <p>Не забывайте о разнообразии рациона и получении необходимых микроэлементов</p>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DailyMealPlan;